// src/components/AddDarkWebFindingModal.js
import React, { useState } from 'react';
import { X, Database, AlertCircle, Key, FileText, MessageSquare } from 'lucide-react';

const FINDING_TYPES = [
  'Stealer Log',
  'PII Exposure',
  'Dark Web Mention'
];

const STATUS_OPTIONS = [
  'Active',
  'Monitoring',
  'Remediated',
  'Resolved',
  'False Positive'
];

const RISK_LEVELS = [
  'Critical',
  'High',
  'Medium',
  'Low',
  'Informational'
];

const MALWARE_FAMILIES = [
  'RedLine Stealer',
  'Raccoon Stealer v2',
  'Vidar Stealer',
  'LummaC2',
  'Mars Stealer',
  'Meta Stealer',
  'Titan Stealer',
  'Other'
];

const AddDarkWebFindingModal = ({ isOpen, onClose, onFindingAdded, companies }) => {
  const [formData, setFormData] = useState({
    companyId: '',
    type: 'Stealer Log',
    title: '',
    status: 'Active',
    risk: 'Medium',
    discovered_date: '',
    source: '',
    source_url: '',
    // Stealer log
    stealer_url: '',
    stealer_username: '',
    stealer_password: '',
    malware_family: '',
    malware_path: '',
    victim_os: '',
    victim_ip: '',
    victim_country: '',
    infection_date: '',
    // PII exposure
    breach_name: '',
    exposed_records: '',
    data_types: '',
    breach_date: '',
    affected_users: '',
    // Dark web mention
    mention_type: '',
    platform: '',
    author_handle: '',
    post_content: '',
    post_date: '',
    threat_indicators: '',
    // Common
    severity_score: '',
    notes: '',
    screenshot_url: '',
    raw_data_url: '',
    remediation_actions: '',
    remediation_date: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setFormData({
      companyId: '',
      type: 'Stealer Log',
      title: '',
      status: 'Active',
      risk: 'Medium',
      discovered_date: '',
      source: '',
      source_url: '',
      stealer_url: '',
      stealer_username: '',
      stealer_password: '',
      malware_family: '',
      malware_path: '',
      victim_os: '',
      victim_ip: '',
      victim_country: '',
      infection_date: '',
      breach_name: '',
      exposed_records: '',
      data_types: '',
      breach_date: '',
      affected_users: '',
      mention_type: '',
      platform: '',
      author_handle: '',
      post_content: '',
      post_date: '',
      threat_indicators: '',
      severity_score: '',
      notes: '',
      screenshot_url: '',
      raw_data_url: '',
      remediation_actions: '',
      remediation_date: ''
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = await window.Clerk?.session?.getToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (!formData.companyId || !formData.title) {
        throw new Error('Company and title are required');
      }

      // Find the company slug from numeric_id
      const selectedCompany = companies.find(c => c.numeric_id === parseInt(formData.companyId));
      if (!selectedCompany) {
        throw new Error('Invalid company selected');
      }

      // Prepare payload based on type
      const payload = {
        type: formData.type,
        title: formData.title,
        status: formData.status,
        risk: formData.risk,
        discovered_date: formData.discovered_date || null,
        source: formData.source || null,
        source_url: formData.source_url || null,
        severity_score: formData.severity_score ? parseInt(formData.severity_score) : null,
        notes: formData.notes || null,
        screenshot_url: formData.screenshot_url || null,
        raw_data_url: formData.raw_data_url || null,
        remediation_actions: formData.remediation_actions || null,
        remediation_date: formData.remediation_date || null
      };

      // Add type-specific fields
      if (formData.type === 'Stealer Log') {
        Object.assign(payload, {
          stealer_url: formData.stealer_url || null,
          stealer_username: formData.stealer_username || null,
          stealer_password: formData.stealer_password || null,
          malware_family: formData.malware_family || null,
          malware_path: formData.malware_path || null,
          victim_os: formData.victim_os || null,
          victim_ip: formData.victim_ip || null,
          victim_country: formData.victim_country || null,
          infection_date: formData.infection_date || null
        });
      } else if (formData.type === 'PII Exposure') {
        Object.assign(payload, {
          breach_name: formData.breach_name || null,
          exposed_records: formData.exposed_records ? parseInt(formData.exposed_records) : null,
          data_types: formData.data_types ? formData.data_types.split(',').map(t => t.trim()) : null,
          breach_date: formData.breach_date || null,
          affected_users: formData.affected_users || null
        });
      } else if (formData.type === 'Dark Web Mention') {
        Object.assign(payload, {
          mention_type: formData.mention_type || null,
          platform: formData.platform || null,
          author_handle: formData.author_handle || null,
          post_content: formData.post_content || null,
          post_date: formData.post_date || null,
          threat_indicators: formData.threat_indicators || null
        });
      }

      const response = await fetch(`/api/companies/${selectedCompany.id}/dark-web`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create dark web finding');
      }

      console.log('Dark web finding created:', data);
      
      if (onFindingAdded) {
        onFindingAdded(data);
      }

      resetForm();
      onClose();
      
      alert(`✅ Dark web finding "${data.finding_id}" created successfully!`);

    } catch (err) {
      console.error('Error creating dark web finding:', err);
      setError(err.message || 'Failed to create dark web finding');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-purple-500/30 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-purple-900/30 to-slate-900/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-400" />
                Add Dark Web Finding
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                Register a new dark web intelligence finding
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="p-2 hover:bg-slate-800/50 rounded-lg transition-all group"
              disabled={loading}
            >
              <X className="w-5 h-5 text-slate-400 group-hover:text-slate-200" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            
            {/* Company & Type Selection */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Company <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.companyId}
                    onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    required
                    disabled={loading}
                  >
                    <option value="">Select a company</option>
                    {companies?.map((company) => (
                      <option key={company.numeric_id} value={company.numeric_id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Finding Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    disabled={loading}
                  >
                    {FINDING_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Common Fields */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Finding Details
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="Brief description of the finding"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Risk Level
                    </label>
                    <select
                      value={formData.risk}
                      onChange={(e) => setFormData({ ...formData, risk: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {RISK_LEVELS.map((risk) => (
                        <option key={risk} value={risk}>{risk}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Severity Score
                    </label>
                    <input
                      type="number"
                      value={formData.severity_score}
                      onChange={(e) => setFormData({ ...formData, severity_score: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="0-100"
                      min="0"
                      max="100"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Discovered Date
                    </label>
                    <input
                      type="date"
                      value={formData.discovered_date}
                      onChange={(e) => setFormData({ ...formData, discovered_date: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Source
                    </label>
                    <input
                      type="text"
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., Russian Market, BreachForums"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Source URL
                    </label>
                    <input
                      type="url"
                      value={formData.source_url}
                      onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="https://..."
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* TYPE-SPECIFIC FIELDS */}
            
            {/* STEALER LOG FIELDS */}
            {formData.type === 'Stealer Log' && (
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-red-400 mb-4 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Stealer Log Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Target URL
                    </label>
                    <input
                      type="url"
                      value={formData.stealer_url}
                      onChange={(e) => setFormData({ ...formData, stealer_url: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="https://login.example.com"
                      disabled={loading}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Username
                      </label>
                      <input
                        type="text"
                        value={formData.stealer_username}
                        onChange={(e) => setFormData({ ...formData, stealer_username: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                        placeholder="user@example.com"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        value={formData.stealer_password}
                        onChange={(e) => setFormData({ ...formData, stealer_password: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                        placeholder="•••••••"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Malware Family
                      </label>
                      <select
                        value={formData.malware_family}
                        onChange={(e) => setFormData({ ...formData, malware_family: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        disabled={loading}
                      >
                        <option value="">Select malware family</option>
                        {MALWARE_FAMILIES.map((family) => (
                          <option key={family} value={family}>{family}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Infection Date
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.infection_date}
                        onChange={(e) => setFormData({ ...formData, infection_date: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Malware Path
                    </label>
                    <input
                      type="text"
                      value={formData.malware_path}
                      onChange={(e) => setFormData({ ...formData, malware_path: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono text-sm"
                      placeholder="C:\Users\...\malware.exe"
                      disabled={loading}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Victim OS
                      </label>
                      <input
                        type="text"
                        value={formData.victim_os}
                        onChange={(e) => setFormData({ ...formData, victim_os: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        placeholder="Windows 11"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Victim IP
                      </label>
                      <input
                        type="text"
                        value={formData.victim_ip}
                        onChange={(e) => setFormData({ ...formData, victim_ip: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                        placeholder="192.168.1.1"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Country
                      </label>
                      <input
                        type="text"
                        value={formData.victim_country}
                        onChange={(e) => setFormData({ ...formData, victim_country: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        placeholder="United States"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PII EXPOSURE FIELDS */}
            {formData.type === 'PII Exposure' && (
              <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-orange-400 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  PII Exposure Details
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Breach Name
                      </label>
                      <input
                        type="text"
                        value={formData.breach_name}
                        onChange={(e) => setFormData({ ...formData, breach_name: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        placeholder="e.g., Company Database 2024"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Breach Date
                      </label>
                      <input
                        type="date"
                        value={formData.breach_date}
                        onChange={(e) => setFormData({ ...formData, breach_date: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Exposed Records
                      </label>
                      <input
                        type="number"
                        value={formData.exposed_records}
                        onChange={(e) => setFormData({ ...formData, exposed_records: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        placeholder="12847"
                        min="0"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Data Types (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={formData.data_types}
                        onChange={(e) => setFormData({ ...formData, data_types: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        placeholder="emails, passwords, SSN"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Affected Users
                    </label>
                    <textarea
                      rows={3}
                      value={formData.affected_users}
                      onChange={(e) => setFormData({ ...formData, affected_users: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="List of affected users or file reference"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* DARK WEB MENTION FIELDS */}
            {formData.type === 'Dark Web Mention' && (
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-purple-400 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Dark Web Mention Details
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Mention Type
                      </label>
                      <input
                        type="text"
                        value={formData.mention_type}
                        onChange={(e) => setFormData({ ...formData, mention_type: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        placeholder="e.g., Forum Post, Marketplace Listing"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Platform
                      </label>
                      <input
                        type="text"
                        value={formData.platform}
                        onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        placeholder="e.g., XSS Forum, BreachForums"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Author Handle
                      </label>
                      <input
                        type="text"
                        value={formData.author_handle}
                        onChange={(e) => setFormData({ ...formData, author_handle: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                        placeholder="username123"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">
                        Post Date
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.post_date}
                        onChange={(e) => setFormData({ ...formData, post_date: e.target.value })}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Post Content
                    </label>
                    <textarea
                      rows={4}
                      value={formData.post_content}
                      onChange={(e) => setFormData({ ...formData, post_content: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="Content of the post or mention..."
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Threat Indicators
                    </label>
                    <input
                      type="text"
                      value={formData.threat_indicators}
                      onChange={(e) => setFormData({ ...formData, threat_indicators: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., targeting, reconnaissance, data exfiltration"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Additional Fields */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Additional Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="Additional analysis and context..."
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Screenshot URL
                    </label>
                    <input
                      type="url"
                      value={formData.screenshot_url}
                      onChange={(e) => setFormData({ ...formData, screenshot_url: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="https://..."
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Raw Data URL
                    </label>
                    <input
                      type="url"
                      value={formData.raw_data_url}
                      onChange={(e) => setFormData({ ...formData, raw_data_url: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="https://..."
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/50 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            disabled={loading}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-slate-200 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !formData.companyId || !formData.title}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              'Create Finding'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddDarkWebFindingModal;